"use client";

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PlatformError } from "./client";
import { platformFetch, platformSend } from "./browser";
import { PAGE_LIMIT_MAX_100, clampLimit, fromCursor, type PageParam } from "./paginate";
import { formatAmount, toBaseUnits, withCurrency, type TokenScale } from "@/lib/format/amount";

export type RewardType =
  | "cashback"
  | "rakeback"
  | "free_spin"
  | "free_money"
  | "bounty"
  | "tier_assignment";

export type RewardStatus =
  | "unverified"
  | "approved"
  | "async"
  | "pending"
  | "wagering"
  | "claimed"
  | "rejected"
  | "expired";

/* `config` is a discriminated union on the same `type` as the reward. Only
   `free_spin` carries anything a player would want to read — the rest is
   bookkeeping the platform needs and we do not. */
export type RewardConfig =
  | { type: Exclude<RewardType, "free_spin">; autoClaim: boolean }
  | {
      type: "free_spin";
      autoClaim: boolean;
      /* How many spins, and what each one stakes — in base units, like every
         other amount on this API. */
      count: number;
      amount: string;
      /* A site-game association id, NOT a catalogue game id — every one probed
         answers 404 from `/games/{gameId}`. It cannot name the game; see
         `freeSpinGameCodes` for what can. */
      siteGameId: string;
      duration: number;
      progress?: number;
      /* The game provider's own answer to activating the spins. The OpenAPI
         document types it `unknown`; `game_codes` inside it is what actually
         names the game, and a game's code is its slug on this platform. */
      response?: unknown;
    };

export type Tranche = {
  status: "active" | "completed" | "exhausted" | "locked" | "cancelled" | "expired";
  remainingWagerAmount: string;
  initialWagerAmount?: string | null;
  /* When a tranche is present its own expiry is the one that counts, and a
     null here means the wagering window is open-ended — the reward's own
     `expiresAt` is about the offer, not the play. */
  expiresAt?: string | null;
};

export type AccountReward = {
  id: string;
  siteBankrollId: string;
  /* Links the reward to the bonus programme that granted it - and through the
     bonus to its rollup, whose wageringProgress states the target the tranche
     alone never does. */
  bonusId?: string | null;
  type: RewardType;
  status: RewardStatus;
  /* "0" while a free spin is unplayed — the value is in `config`. Once played
     and wagering, this becomes the credited winnings. */
  amount: string;
  createdAt: string;
  claimedAt?: string;
  expiresAt?: string;
  config?: RewardConfig;
  tranche?: Tranche | null;
};

export type Rakeback = {
  total: string;
  currentMonthTotal: string;
  totalClaimed: string;
  claimable: string;
};

/* What the panel shows. `/incentives/rewards` filters to `approved` when no
   status is asked for, so without this list a reward vanishes from the screen
   the instant it is claimed and the page reads "No rewards yet" while the
   player holds live free spins. `unverified` stays out: it is the state before
   the platform has decided a reward is owed at all. */
export const VISIBLE_STATUSES: RewardStatus[] = [
  "approved",
  "pending",
  "wagering",
  "async",
  "claimed",
  "expired",
  "rejected",
];

/* Only `approved` is claimable. The platform's claim query filters on it and
   answers 404 NO_CLAIMABLE_REWARDS_FOUND for anything else, so offering a
   button on any other status promises something that cannot happen. */
export const CLAIMABLE_STATUSES: RewardStatus[] = ["approved"];

type RewardsEnvelope = { data: AccountReward[]; next?: string | null };

/* What the active surfaces show. Claimed and expired rewards belong to the
   history panel, the way the whitelabel's active list keeps to pending,
   approved, wagering and async — an expired reward in the live list is an
   offer the player can no longer take. */
export const ACTIVE_REWARD_STATUSES: RewardStatus[] = ["pending", "approved", "wagering", "async"];

/* Whether the reward can still be acted on. A tranche, when there is one,
   owns the expiry: its `expiresAt` bounds the wagering window, and null there
   means open-ended, whatever the offer's own date says. Only a reward with no
   tranche — or one whose tranche does not state an expiry — falls back to its
   own `expiresAt`. Mirrors the whitelabel's isExpiredReward. */
export const isExpiredReward = (reward: AccountReward, now: number): boolean => {
  const tranche = reward.tranche;

  if (tranche && tranche.expiresAt !== undefined) {
    return tranche.expiresAt !== null && Date.parse(tranche.expiresAt) <= now;
  }

  return reward.expiresAt !== undefined && Date.parse(reward.expiresAt) <= now;
};

/* A wagering reward whose tranche has been spent to nothing. The status still
   says `wagering`, but there is no play left in it and nothing to show for
   it, so the active list drops it — as the whitelabel's does. */
export const isExhaustedWagering = (reward: AccountReward): boolean =>
  reward.status === "wagering" && reward.tranche?.status === "exhausted";

/* A tranche's own wagering progress, measured.

   The tranche states both the requirement it began with and what is left, so
   the bar is computed rather than guessed — the same arithmetic the
   whitelabel's `getWageringProgress` makes. This exists because the strip used
   to render full whenever no rollup was found, which reads as "requirement
   met" on a reward that has not been wagered at all.

   Clamped at both ends on purpose: late winnings top a live tranche up
   (`incrementManyWageringTrancheAmounts` raises both the initial and the
   remaining), so `remaining` can briefly exceed `initial` and the subtraction
   go negative. A tranche the platform states no initial requirement for
   yields nothing, and the caller shows the figure alone. */
export const trancheProgressView = (
  tranche: Tranche | null | undefined,
  scale?: TokenScale & { symbol: string },
) => {
  const initial = toBaseUnits(tranche?.initialWagerAmount);

  if (!tranche || initial <= 0n) {
    return null;
  }

  const remaining = toBaseUnits(tranche.remainingWagerAmount);
  const raw = initial - remaining;
  const wagered = raw < 0n ? 0n : raw > initial ? initial : raw;

  return {
    percent: Number((wagered * 10_000n) / initial) / 100,
    note: scale
      ? `${withCurrency(formatAmount(wagered.toString(), scale), scale.symbol)}/${withCurrency(formatAmount(initial.toString(), scale), scale.symbol)}`
      : `${wagered}/${initial}`,
  };
};

export const isActiveReward = (reward: AccountReward, now: number): boolean =>
  ACTIVE_REWARD_STATUSES.includes(reward.status) &&
  !isExpiredReward(reward, now) &&
  !isExhaustedWagering(reward);

/* The game codes a free spin is good for, out of the provider's untyped
   `response`. Narrowed by hand rather than asserted: anything not the shape
   we expect yields no codes, and a reward with no codes still renders — it
   just cannot link to a game. */
export const freeSpinGameCodes = (reward: AccountReward): string[] => {
  const config = reward.config;

  if (config?.type !== "free_spin") {
    return [];
  }

  const response = config.response;

  if (response === null || response === undefined || typeof response !== "object") {
    return [];
  }

  if (!("game_codes" in response)) {
    return [];
  }

  const codes = (response as { game_codes: unknown }).game_codes;

  if (!Array.isArray(codes)) {
    return [];
  }

  return codes.filter((code): code is string => typeof code === "string");
};

/* What the reward's one button does. This is the whitelabel's state machine,
   and the part the previous button got wrong: it offered Claim for everything
   and enabled it only on `approved`, so an activated free spin — status
   `async`, the state a granted spin actually sits in — showed a Claim that
   could never fire. Activated spins are played, not claimed; the button is a
   link to the game. And an `approved` reward the platform will auto-claim is
   not the player's to claim either — it is in flight. */
export type RewardAction =
  | { kind: "claim"; label: "Claim" }
  | { kind: "play"; label: "Play"; href: string }
  | {
      kind: "disabled";
      label: "Claiming…" | "Processing…" | "In progress" | "Expired";
      loading?: boolean;
    };

export const rewardAction = (
  reward: AccountReward,
  now: number,
  { claimingId = null, playHref }: { claimingId?: string | null; playHref?: string } = {},
): RewardAction => {
  if (claimingId === reward.id) {
    return { kind: "disabled", label: "Claiming…", loading: true };
  }

  if (isExpiredReward(reward, now)) {
    return { kind: "disabled", label: "Expired" };
  }

  switch (reward.status) {
    case "approved":
      if (reward.config?.autoClaim) {
        return { kind: "disabled", label: "Processing…", loading: true };
      }

      return { kind: "claim", label: "Claim" };

    case "async": {
      /* A game's code is its slug here, so the code routes directly; a
         resolved slug from the catalogue is preferred when the caller has one. */
      const code = freeSpinGameCodes(reward).at(0);

      return { kind: "play", label: "Play", href: playHref ?? (code ? `/game/${code}` : "/") };
    }

    case "pending":
      return { kind: "disabled", label: "Processing…", loading: true };

    default:
      return { kind: "disabled", label: "In progress" };
  }
};

export const useAccountRewards = (
  enabled: boolean,
  { limit = 25, refetchInterval }: { limit?: number; refetchInterval?: number } = {},
) => {
  const result = useInfiniteQuery({
    queryKey: ["incentives", "rewards", limit],
    enabled,
    staleTime: 30_000,
    refetchInterval,
    initialPageParam: undefined as PageParam | undefined,
    queryFn: ({ pageParam }) =>
      platformFetch<RewardsEnvelope>("incentives/rewards", {
        limit: clampLimit(limit, PAGE_LIMIT_MAX_100),
        status: VISIBLE_STATUSES,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => fromCursor(lastPage).nextPageParam,
  });

  return {
    ...result,
    rewards: (result.data?.pages ?? []).flatMap((page) => page.data),
  };
};

export const useRakeback = (bankrollId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["rewards", "rakeback", bankrollId],
    enabled: enabled && Boolean(bankrollId),
    staleTime: 30_000,
    queryFn: async () =>
      (await platformFetch<{ data: Rakeback }>(`rewards/rakeback/${bankrollId}`)).data,
  });

type ClaimedReward = {
  id: string;
  bonusId: string | null;
  recurrenceKey: string | null;
  period: string | null;
  status: RewardStatus;
  amount: string;
};

/* A claim is per-reward, and a reward that could not be claimed does not fail
   the request — it comes back in `errors` beside the ones that worked, under
   HTTP 200. */
export type ClaimRewardsResult = {
  data: ClaimedReward[];
  errors?: { rewardId?: string; code: string; reason?: string }[];
};

export const useClaimRewards = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (rewardIds: string[]) => {
      const result = await platformSend<ClaimRewardsResult>("incentives/rewards/claim", {
        rewardIds,
      });

      const errors = result.errors ?? [];

      /* Nothing claimed is a failed claim, whatever the status line said.
         A partial result stays a success and reports the shortfall. */
      if (result.data.length === 0 && errors.length > 0) {
        throw new PlatformError(200, "invalid", errors[0].reason ?? errors[0].code);
      }

      return result;
    },
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["incentives", "rewards"] });
      client.invalidateQueries({ queryKey: ["rewards"] });
      client.invalidateQueries({ queryKey: ["balances"] });
    },
  });
};

export const useClaimRakeback = (bankrollId: string | undefined) => {
  const client = useQueryClient();

  return useMutation({
    /* `amount` is required — an empty body is refused with "expected string,
       received undefined". The caller passes back the `claimable` figure the
       API itself published, so no scale is invented at this call site. */
    mutationFn: (amount: string) =>
      platformSend<{ data: unknown }>(`rewards/rakeback/${bankrollId}/claim`, { amount }),
    /* The whole `rewards` prefix, not just `rewards/rakeback`: a claim also
       writes a row into `/rewards/history`, whose key is a sibling rather than
       a child, so a narrower key leaves the history panel showing the state
       before the claim. */
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["rewards"] });
      client.invalidateQueries({ queryKey: ["balances"] });
    },
  });
};

export type CustomRewards = { total: string; totalClaimed: string; claimable: string };

export const useCustomRewards = (bankrollId: string | undefined, enabled: boolean) =>
  useQuery({
    queryKey: ["rewards", "custom", bankrollId],
    enabled: enabled && Boolean(bankrollId),
    staleTime: 30_000,
    queryFn: async () =>
      (await platformFetch<{ data: CustomRewards }>(`rewards/custom-rewards/${bankrollId}`)).data,
  });

/* Counts of rewards, not amounts. */
export type CustomRewardsClaim = { claimed: number; unclaimed: number };

export const useClaimCustomRewards = (bankrollId: string | undefined) => {
  const client = useQueryClient();

  return useMutation({
    /* This body is validated against `null`, not against a shape. Sending the
       `{}` every other write here sends is refused with "expected null,
       received object" — a 400 on every single claim. The OpenAPI document
       types the body `unknown`, so nothing but the running API says so. */
    mutationFn: () =>
      platformSend<{ data: CustomRewardsClaim }>(
        `rewards/custom-rewards/${bankrollId}/claim`,
        null,
      ),
    onSettled: () => {
      client.invalidateQueries({ queryKey: ["rewards"] });
      client.invalidateQueries({ queryKey: ["balances"] });
    },
  });
};

/* What a reward row states, kept pure so the field mapping is testable without
   a session — the same shape as `tierProgressView` in incentives.ts.

   Two things the payload does not hand over directly: a free spin's worth is
   `config.count` × `config.amount` rather than the top-level `amount`, which is
   always "0"; and `status` is a platform word (`async`, `wagering`) that means
   nothing to a player. */
export const TYPE_LABEL: Record<RewardType, string> = {
  cashback: "Cashback",
  rakeback: "Rakeback",
  free_spin: "Free spins",
  free_money: "Bonus funds",
  bounty: "Bounty",
  tier_assignment: "Tier reward",
};

const STATUS_LABEL: Record<RewardStatus, string> = {
  unverified: "Being checked",
  approved: "Ready to claim",
  pending: "Being awarded",
  async: "Active",
  wagering: "Wagering",
  claimed: "Claimed",
  rejected: "Not awarded",
  expired: "Expired",
};

export const rewardView = (
  reward: AccountReward,
  now: number,
  scale?: TokenScale & { symbol: string },
) => {
  const config = reward.config;
  const spins = config?.type === "free_spin" ? config : null;
  const isExpired = isExpiredReward(reward, now);
  const isWageringSpin = spins !== null && reward.status === "wagering";

  const amount = (value: string) =>
    scale ? withCurrency(formatAmount(value, scale), scale.symbol) : value;

  const wageringDetail = () => {
    const tranche = reward.tranche;

    if (!tranche || tranche.status === "expired" || tranche.status === "cancelled") {
      return "Wagering requirement expired";
    }

    return `${amount(tranche.remainingWagerAmount)} left to wager`;
  };

  return {
    title: TYPE_LABEL[reward.type] ?? reward.type,
    status: isExpired ? STATUS_LABEL.expired : (STATUS_LABEL[reward.status] ?? reward.status),
    /* A free spin is a count, not a credit — stating "0.00 USDSM" beside
       "Free spins" claims a value, and the value it claims is wrong. */
    value: isWageringSpin ? amount(reward.amount) : spins ? `${spins.count} spins` : amount(reward.amount),
    detail: isWageringSpin ? wageringDetail() : spins ? `${amount(spins.amount)} per spin` : null,
    gameId: spins?.siteGameId ?? null,
    gameCode: freeSpinGameCodes(reward).at(0) ?? null,
    /* Claimable means the button would fire: approved, not auto-claimed by
       the platform, and not expired — the same test rewardAction makes. */
    isClaimable: rewardAction(reward, now).kind === "claim",
    isExpired,
  };
};
